/**
 * FILE: packages\supabase\src\services\courseAttempts.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */

/**
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 */

/**
 * Course attempts registry — Hub dropdowns and ops visibility.
 */

import {
  canonicalAttemptForStorage,
  canonicalCourseForStorage,
  filterAttemptsForUiDropdown,
  isFutureOrCurrentAttempt,
  isValidStorageAttempt,
} from '@fintree/utils'
import { ServiceError } from '../internal/errors'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";

export interface CourseAttemptRow {
  course: string
  attempt: string
  displayAttempt: string
  sources: string[]
  studentCount: number
  isActive: boolean
  catalogSeenAt: string | null
  firstSeenAt: string | null
  updatedAt: string | null
}

type CourseAttemptDbRow = {
  course: string
  attempt: string
  display_attempt: string
  sources: string[] | null
  student_count: number | null
  is_active: boolean | null
  catalog_seen_at: string | null
  first_seen_at: string | null
  updated_at: string | null
}

function toRow(row: CourseAttemptDbRow): CourseAttemptRow {
  return {
    course: row.course,
    attempt: row.attempt,
    displayAttempt: row.display_attempt,
    sources: row.sources ?? [],
    studentCount: row.student_count ?? 0,
    isActive: row.is_active ?? true,
    catalogSeenAt: row.catalog_seen_at,
    firstSeenAt: row.first_seen_at,
    updatedAt: row.updated_at,
  }
}

export async function fetchAttemptsForCourse(
  course: string,
  opts?: { includeInactive?: boolean; uiDropdown?: boolean }, client: DbClient = defaultDbClient,
): Promise<CourseAttemptRow[]> {
  const canonical = canonicalCourseForStorage(course)
  let q = client
    .from('course_attempts')
    .select('*')
    .eq('course', canonical)
    .order('attempt', { ascending: true })

  if (!opts?.includeInactive) {
    q = q.eq('is_active', true)
  }

  const { data, error } = await q
  if (error) throw new ServiceError(error.message, 'COURSE_ATTEMPTS_FETCH', error)

  let rows = (data ?? []).map((row) => toRow(row as CourseAttemptDbRow))

  if (opts?.uiDropdown !== false) {
    const allowed = new Set(filterAttemptsForUiDropdown(rows.map((r) => r.attempt)))
    rows = rows.filter((r) => allowed.has(r.attempt))
  }

  return rows
}

export async function fetchAllActiveAttempts(client: DbClient = defaultDbClient): Promise<CourseAttemptRow[]> {
  const { data, error } = await client
    .from('course_attempts')
    .select('*')
    .eq('is_active', true)
    .order('course', { ascending: true })
    .order('attempt', { ascending: true })

  if (error) throw new ServiceError(error.message, 'COURSE_ATTEMPTS_ALL', error)
  return (data ?? []).map((row) => toRow(row as CourseAttemptDbRow))
}

export async function fetchNewAttemptsSince(since: string, client: DbClient = defaultDbClient): Promise<CourseAttemptRow[]> {
  const { data, error } = await client
    .from('course_attempts_changelog')
    .select('course, attempt, event, created_at')
    .eq('event', 'discovered')
    .gte('created_at', since)
    .order('created_at', { ascending: false })

  if (error) throw new ServiceError(error.message, 'COURSE_ATTEMPTS_CHANGELOG', error)

  const pairs = new Map<string, { course: string; attempt: string }>()
  for (const row of data ?? []) {
    pairs.set(`${row.course}||${row.attempt}`, { course: row.course, attempt: row.attempt })
  }
  if (!pairs.size) return []

  const { data: attempts, error: attemptsError } = await client
    .from('course_attempts')
    .select('*')
    .in(
      'course',
      [...pairs.values()].map((p) => p.course),
    )

  if (attemptsError) throw new ServiceError(attemptsError.message, 'COURSE_ATTEMPTS_NEW', attemptsError)

  const wanted = new Set([...pairs.keys()])
  return (attempts ?? [])
    .filter((row) => wanted.has(`${row.course}||${row.attempt}`))
    .map((row) => toRow(row as CourseAttemptDbRow))
    .filter(
      (row) =>
        isValidStorageAttempt(row.attempt) &&
        (isFutureOrCurrentAttempt(row.attempt) || isFutureOrCurrentAttempt(row.displayAttempt)),
    )
}

export async function addManualCourseAttempt(course: string, attempt: string, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client.rpc('upsert_course_attempt_registry', {
    p_course: canonicalCourseForStorage(course),
    p_attempt: canonicalAttemptForStorage(attempt),
    p_source: 'manual',
  })
  if (error) throw new ServiceError(error.message, 'COURSE_ATTEMPT_MANUAL', error)
}

/** Manual registry upsert only — automatic paths use DB triggers. */
export async function upsertCourseAttemptRegistry(
  course: string,
  attempt: string | null | undefined,
  source: 'enrollment' | 'student' | 'manual' = 'enrollment', client: DbClient = defaultDbClient,
): Promise<void> {
  if (!attempt?.trim() || !course?.trim()) return
  const { error } = await client.rpc('upsert_course_attempt_registry', {
    p_course: canonicalCourseForStorage(course),
    p_attempt: canonicalAttemptForStorage(attempt),
    p_source: source,
  })
  if (error) throw new ServiceError(error.message, 'COURSE_ATTEMPT_UPSERT', error)
}
